import type {EstadoDeProvisiones, Provision, ResultadoDeGeneracion} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {
  RUTA_DEL_CONSOLIDADO_DE_PROVISIONES,
  rutaDeDocumentoGenerado,
  useCambiarProvision,
  useEstadoDeProvisiones,
  useGenerarProvisiones,
} from '../api/documentos';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {usePermisos} from './sesion-contexto';

const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50';
const campo = 'mt-1 w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta';

const porcentaje = (n: number | null, decimales = 2) => (n === null ? '—' : `${numero(n * 100, decimales)} %`);
const mensajeDe = (error: unknown) =>
  error instanceof ErrorDeApi
    ? [error.message, ...(error.detalles ?? [])].join(' ')
    : tr('No se pudo generar.', 'Could not generate.');

// Provisiones de concentrados de Mona Minas (decision 0099). En EXW la
// provision sale con la instruccion de facturacion; en CIF o FOB se hace aqui,
// despues de facturar, con las facturas en PDF.
export function ProvisionesDeExportacion({
  codigo,
  onResultado,
}: {
  codigo: string;
  onResultado: (r: ResultadoDeGeneracion) => void;
}) {
  const permisos = usePermisos();
  const estado = useEstadoDeProvisiones(codigo);
  const [abierto, setAbierto] = useState(false);
  if (estado.isError) return <p className="mt-3 text-sm text-alerta">{estado.error.message}</p>;
  if (!estado.data) return null;
  const e = estado.data;
  const esExw = e.incoterm?.startsWith('EXW') ?? false;
  return (
    <section className="mt-5 rounded-lg border border-linea p-4" aria-label={tr('Provisiones', 'Provisions')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium">{tr('Provisiones', 'Provisions')}</h3>
        <a href={RUTA_DEL_CONSOLIDADO_DE_PROVISIONES} className="text-xs text-verde hover:underline" download>
          ↓ {tr('Consolidado histórico (Excel)', 'Historical consolidated (Excel)')}
        </a>
      </div>
      <p className="mt-1 text-xs text-apagado">
        {esExw
          ? tr(
              'Esta exportación es EXW: la provisión se hace al generar la instrucción de facturación, que resta sus descuentos (deducciones metálicas, otros cargos y penalidades).',
              'This export is EXW: the provision is made when generating the invoicing instruction, which subtracts its discounts (metal deductions, other charges and penalties).',
            )
          : tr(
              'En CIF o FOB la provisión se hace después de facturar: una por factura, con el Randomizer y los Niton Results. Las facturas se suben en PDF.',
              'In CIF or FOB the provision is made after invoicing: one per invoice, with the Randomizer and the Niton Results. Invoices are uploaded as PDF.',
            )}
      </p>
      {!esExw && (
        <p className="mt-1 text-xs text-apagado">
          {tr(
            `Facturas leídas: ${numero(e.facturas.length)}${e.facturas.length ? ` (${e.facturas.map(f => `${f.numero}: ${numero(f.bigBags)} BB`).join(', ')})` : ''}. Big Bags sin factura: ${numero(e.bigBagsSinFactura)} de ${numero(e.bigBags)}.`,
            `Invoices read: ${numero(e.facturas.length)}${e.facturas.length ? ` (${e.facturas.map(f => `${f.numero}: ${numero(f.bigBags)} BB`).join(', ')})` : ''}. Big Bags without an invoice: ${numero(e.bigBagsSinFactura)} of ${numero(e.bigBags)}.`,
          )}
        </p>
      )}
      {permisos.editar && !esExw && (
        <div className="mt-3">
          {abierto ? (
            <FormularioDeProvisiones
              codigo={codigo}
              estado={e}
              onCerrar={() => setAbierto(false)}
              onResultado={onResultado}
            />
          ) : (
            <button type="button" className={botonSuave} onClick={() => setAbierto(true)}>
              ↓ {tr('Generar provisiones', 'Generate provisions')}
            </button>
          )}
        </div>
      )}
      {e.provisiones.length > 0 && <TablaDeProvisiones provisiones={e.provisiones} conCodigo={false} />}
    </section>
  );
}

function FormularioDeProvisiones({
  codigo,
  estado,
  onCerrar,
  onResultado,
}: {
  codigo: string;
  estado: EstadoDeProvisiones;
  onCerrar: () => void;
  onResultado: (r: ResultadoDeGeneracion) => void;
}) {
  const generar = useGenerarProvisiones(codigo);
  const s = estado.valoresSugeridos;
  const [trm, setTrm] = useState(s ? String(s.trm) : '');
  const [oro, setOro] = useState(s ? String(s.precioOro) : '');
  const [plata, setPlata] = useState(s ? String(s.precioPlata) : '');
  const [facturas, setFacturas] = useState<File[]>([]);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const n = (t: string) => Number(t.replace(',', '.'));
  return (
    <form
      className="space-y-3 rounded-lg border border-linea bg-papel p-4"
      aria-label={tr('Generar provisiones', 'Generate provisions')}
      onSubmit={async ev => {
        ev.preventDefault();
        setMensaje(null);
        try {
          const r = await generar.mutateAsync({facturas, trm: n(trm), precioOro: n(oro), precioPlata: n(plata)});
          onResultado(r.generacion);
          onCerrar();
        } catch (error) {
          setMensaje(mensajeDe(error));
        }
      }}
    >
      <label className="block text-xs text-apagado">
        {tr('Facturas en PDF (MMFE…)', 'Invoices as PDF (MMFE…)')}
        <input
          type="file"
          accept=".pdf,application/pdf"
          multiple
          onChange={ev => setFacturas([...(ev.target.files ?? [])])}
          className={campo}
          aria-label={tr('Facturas en PDF', 'Invoices as PDF')}
        />
      </label>
      <p className="text-xs text-apagado">
        {estado.facturas.length
          ? tr(
              'Ya hay facturas leídas: puedes generar sin subir nada, o subirlas otra vez si cambiaron.',
              'There are invoices already read: you can generate without uploading anything, or upload them again if they changed.',
            )
          : tr(
              'Sube una factura por mina: la app la lee, le asigna sus Big Bags por el oro y la concilia con el control de empaque.',
              'Upload one invoice per mine: the app reads it, assigns its Big Bags by the gold and reconciles it with the packing control.',
            )}
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-xs text-apagado">
          TRM
          <input
            type="number"
            step="0.01"
            min="0"
            required
            value={trm}
            onChange={ev => setTrm(ev.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('FIX oro (USD/oz)', 'Gold FIX (USD/oz)')}
          <input
            type="number"
            step="0.01"
            min="0"
            required
            value={oro}
            onChange={ev => setOro(ev.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('FIX plata (USD/oz)', 'Silver FIX (USD/oz)')}
          <input
            type="number"
            step="0.001"
            min="0"
            required
            value={plata}
            onChange={ev => setPlata(ev.target.value)}
            className={campo}
          />
        </label>
      </div>
      {s && (
        <p className="text-xs text-apagado">
          {tr(
            'FIX y TRM tomados de la última instrucción de facturación de la exportación.',
            'FIX and TRM taken from the latest invoicing instruction of the export.',
          )}
        </p>
      )}
      {!estado.conLogo && (
        <p className="text-xs text-apagado">
          ⚠{' '}
          {tr(
            'No hay logo del comprador (Administración → Documentos): la hoja Format sale sin logo.',
            'There is no buyer logo (Administration → Documents): the Format sheet comes out without a logo.',
          )}
        </p>
      )}
      {mensaje && <p className="text-sm text-alerta">{mensaje}</p>}
      <div className="flex gap-2">
        <button type="submit" className={boton} disabled={generar.isPending}>
          {generar.isPending ? tr('Generando…', 'Generating…') : tr('Generar', 'Generate')}
        </button>
        <button type="button" className={botonSuave} onClick={onCerrar}>
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}

// Una fila por provision con la comparacion contra la factura (solo el valor
// mineral: pagable de oro + pagable de plata) y aprobar o retirar del consolidado.
export function TablaDeProvisiones({provisiones, conCodigo}: {provisiones: Provision[]; conCodigo: boolean}) {
  const permisos = usePermisos();
  const cambiar = useCambiarProvision();
  const [detalle, setDetalle] = useState<number | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const accion = async (id: number, que: 'aprobar' | 'retirar') => {
    setMensaje(null);
    try {
      await cambiar.mutateAsync({id, accion: que});
    } catch (error) {
      setMensaje(mensajeDe(error));
    }
  };
  const color = (p: Provision) =>
    p.estado === 'alerta'
      ? 'text-alerta'
      : p.estado === 'aprobada'
        ? 'text-verde-oscuro'
        : p.estado === 'retirada'
          ? 'text-apagado'
          : '';
  return (
    <div className="mt-3 overflow-x-auto">
      {mensaje && <p className="mb-2 text-sm text-alerta">{mensaje}</p>}
      <table className="min-w-full text-xs">
        <thead className="text-left text-apagado">
          <tr>
            {conCodigo && <th className="px-2 py-1">{tr('Exportación', 'Export')}</th>}
            <th className="px-2 py-1">{tr('Factura', 'Invoice')}</th>
            <th className="px-2 py-1">{tr('Grupo', 'Group')}</th>
            <th className="px-2 py-1 text-right">BB</th>
            <th className="px-2 py-1 text-right">DMT</th>
            <th className="px-2 py-1 text-right">{tr('Rebaja Au / Ag', 'Au / Ag reduction')}</th>
            <th className="px-2 py-1 text-right">{tr('Valor mineral factura', 'Invoice mineral value')}</th>
            <th className="px-2 py-1 text-right">{tr('Valor mineral provisión', 'Provision mineral value')}</th>
            <th className="px-2 py-1 text-right">{tr('Diferencia', 'Difference')}</th>
            <th className="px-2 py-1 text-right">{tr('Provisión neta USD', 'Net provision USD')}</th>
            <th className="px-2 py-1">{tr('Estado', 'Status')}</th>
            <th className="px-2 py-1" />
          </tr>
        </thead>
        <tbody>
          {provisiones.map(p => (
            <FilaDeProvision
              key={p.id}
              p={p}
              conCodigo={conCodigo}
              color={color(p)}
              abierto={detalle === p.id}
              onDetalle={() => setDetalle(d => (d === p.id ? null : p.id))}
              puedeEditar={permisos.editar}
              ocupado={cambiar.isPending}
              onAccion={que => void accion(p.id, que)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilaDeProvision({
  p,
  conCodigo,
  color,
  abierto,
  onDetalle,
  puedeEditar,
  ocupado,
  onAccion,
}: {
  p: Provision;
  conCodigo: boolean;
  color: string;
  abierto: boolean;
  onDetalle: () => void;
  puedeEditar: boolean;
  ocupado: boolean;
  onAccion: (que: 'aprobar' | 'retirar') => void;
}) {
  const columnas = conCodigo ? 13 : 12;
  return (
    <>
      <tr className="border-t border-linea">
        {conCodigo && <td className="px-2 py-1">{p.codigo}</td>}
        <td className="px-2 py-1">
          {p.documentoId ? (
            <a href={rutaDeDocumentoGenerado(p.documentoId)} className="text-verde hover:underline" download>
              ↓ {p.factura}
            </a>
          ) : (
            p.factura
          )}
          {p.fechaFactura ? <span className="ml-1 text-apagado">{fecha(p.fechaFactura)}</span> : null}
        </td>
        <td className="px-2 py-1">{p.grupo ?? '—'}</td>
        <td className="px-2 py-1 text-right">{numero(p.bigBags)}</td>
        <td className="px-2 py-1 text-right">{numero(p.dmt, 3)}</td>
        <td className="px-2 py-1 text-right">
          {porcentaje(p.rebajaAu)} / {porcentaje(p.rebajaAg)}
        </td>
        <td className="px-2 py-1 text-right">
          {p.valorMineralFactura === null ? '—' : numero(p.valorMineralFactura, 2)}
        </td>
        <td className="px-2 py-1 text-right">{numero(p.valorMineralProvision, 2)}</td>
        <td className="px-2 py-1 text-right">
          {p.diferenciaUsd === null ? '—' : `${numero(p.diferenciaUsd, 2)} (${porcentaje(p.diferenciaPct)})`}
        </td>
        <td className="px-2 py-1 text-right">{numero(p.netoUsd, 2)}</td>
        <td className={`px-2 py-1 font-medium ${color}`}>{p.estadoEtiqueta}</td>
        <td className="whitespace-nowrap px-2 py-1 text-right">
          <button type="button" className="text-verde hover:underline" onClick={onDetalle}>
            {abierto ? tr('Ocultar', 'Hide') : tr('Validación', 'Validation')}
          </button>
          {puedeEditar && p.estado === 'validada' && (
            <button
              type="button"
              className="ml-2 text-verde hover:underline disabled:opacity-50"
              disabled={ocupado}
              onClick={() => onAccion('aprobar')}
            >
              ✓ {tr('Aprobar', 'Approve')}
            </button>
          )}
          {puedeEditar && p.estado === 'retirada' && (
            <button
              type="button"
              className="ml-2 text-verde hover:underline disabled:opacity-50"
              disabled={ocupado}
              onClick={() => onAccion('aprobar')}
            >
              ✓ {tr('Aprobar otra vez', 'Approve again')}
            </button>
          )}
          {puedeEditar && p.estado === 'aprobada' && (
            <button
              type="button"
              className="ml-2 text-apagado hover:underline disabled:opacity-50"
              disabled={ocupado}
              onClick={() => onAccion('retirar')}
            >
              {tr('Retirar del consolidado', 'Withdraw from consolidated')}
            </button>
          )}
        </td>
      </tr>
      {abierto && (
        <tr>
          <td colSpan={columnas} className="bg-papel px-2 py-2">
            {p.alertas.length > 0 && (
              <ul className="mb-2 space-y-0.5 text-alerta">
                {p.alertas.map(a => (
                  <li key={a}>⚠ {a}</li>
                ))}
              </ul>
            )}
            <table className="text-xs">
              <thead className="text-left text-apagado">
                <tr>
                  <th className="pr-3">{tr('Control', 'Check')}</th>
                  <th className="pr-3">{tr('Esperado', 'Expected')}</th>
                  <th className="pr-3">{tr('Calculado', 'Calculated')}</th>
                  <th className="pr-3">{tr('Estado', 'Status')}</th>
                  <th>{tr('Observación', 'Note')}</th>
                </tr>
              </thead>
              <tbody>
                {p.controles.map(c => (
                  <tr key={c.control}>
                    <td className="pr-3">{c.control}</td>
                    <td className="pr-3">{typeof c.esperado === 'number' ? numero(c.esperado, 3) : c.esperado}</td>
                    <td className="pr-3">{typeof c.calculado === 'number' ? numero(c.calculado, 3) : c.calculado}</td>
                    <td className={`pr-3 font-medium ${c.estado === 'ALERTA' ? 'text-alerta' : ''}`}>{c.estado}</td>
                    <td>{c.observacion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
